import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Check } from 'assets/icons/Check.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import { clampRoleEditorWorkspacePermissions } from 'modules/CreateWorkspaceModal/utils';
// Pure function despite the `use*` prefix (spec/tech.md §2.2 — "not a hook"). Imported under a
// non-`use` alias so it can be invoked inside the async submit callback without tripping
// `react-hooks/rules-of-hooks` for a value-dependent call site.
import getScopedPowerLevels from 'modules/CreateWorkspaceModal/WorkspaceRoleCreate/useScopedPowerLevels';
import { DefaultRoleTypesEnum } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { canSendStateEvent, CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { PermissionId } from 'utils/powerLevelsConfig';
import { WORKSPACE_ROLES_EVENT_VERSION } from 'utils/workspaceRolesEvent';

type UpdateWorkspaceRolePayload = {
  workspaceId: string;
  roleId: string;
  name: string;
  permissions: Record<PermissionId, boolean>;
};

const useUpdateWorkspaceRole = () => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const {
    workspaceRoles,
    roleAssignments,
    workspaceId: contextWorkspaceId,
  } = useWorkspacesContext({
    requireWorkspaceId: true,
  });
  const actorPowerLevels = useScopedUserPowerLevels(contextWorkspaceId);

  const updateWorkspaceRole = async ({ workspaceId, roleId, name, permissions }: UpdateWorkspaceRolePayload) => {
    const workspaceRoom = matrixClient.getRoom(workspaceId);
    const currentUserId = matrixClient.getUserId();
    if (!workspaceRoom || !currentUserId) return;

    const roleToUpdate = workspaceRoles.find((candidate) => candidate.id === roleId);
    if (
      roleToUpdate?.defaultRoleType === DefaultRoleTypesEnum.OWNER ||
      roleToUpdate?.defaultRoleType === DefaultRoleTypesEnum.MEMBER
    ) {
      return;
    }

    setIsLoading(true);

    try {
      // V2 gate (AC-081a / Decision 18): only the workspace-roles send permission matters.
      // The V1 `canSendPowerLevels` check is removed — V2 frontend never writes m.room.power_levels.
      const canSendAssignment = canSendStateEvent(
        workspaceRoom,
        CustomMeedioMatrixEvents.WORKSPACE_ROLES,
        currentUserId
      );

      if (!canSendAssignment) {
        return pushToast({
          variant: 'info',
          title: t('workspaces.no_permission'),
          description: t('workspaces.no_permission_description'),
        });
      }

      const powerLevels = getScopedPowerLevels(
        clampRoleEditorWorkspacePermissions(permissions, actorPowerLevels.workspace)
      );
      const updatedRoles = workspaceRoles.map((role) => (role.id === roleId ? { ...role, name, powerLevels } : role));

      await matrixClient.sendStateEvent(workspaceId, CustomMeedioMatrixEvents.WORKSPACE_ROLES, {
        version: WORKSPACE_ROLES_EVENT_VERSION,
        roles: updatedRoles,
        assignments: roleAssignments,
      });

      pushToast({ icon: Check, variant: 'success', title: t('workspaces_role_update_success') });
    } catch (error) {
      logger.error('Failed to update workspace role', error);
      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
    } finally {
      setIsLoading(false);
    }
  };

  return { updateWorkspaceRole, isLoading };
};

export default useUpdateWorkspaceRole;
